import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";

import { API, useAuth } from "@/context/AuthContext";
import {
  calculateSellingPrice,
  isBeanie,
  isLongSleeve,
  resolveCommercialGroup,
  resolveMixPool,
  MIX_POOL_MIN,
} from "@/lib/commercialEngine";

/**
 * CatalogProductDetail · variant configurator backed by the local Mongo
 * mirror + LIVE SOAP pricing/inventory.
 *
 * Consumes the browse endpoint:
 *   GET /api/catalog/browse/product/{style}
 *
 * Response shape:
 *   { style, title, brand, category, sub_category, description,
 *     default_image, sizes[], colors[ { color_name, color_code,
 *       swatch_url, product_image, sizes[], ... } ],
 *     pricing: { sku: {...} }, inventory: { sku: {...} },
 *     synced_at }
 *
 * Colors are rendered from the ACTUAL `swatch_url` image (never a hex
 * fallback). Live pricing per (color, size) is displayed inline; live
 * inventory is aggregated below.
 */

function ColorSwatch({ color, active, onClick, testId, badge }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      title={color.color_name}
      aria-label={color.color_name}
      aria-pressed={active}
      className={`relative w-9 h-9 rounded-full border-2 overflow-hidden transition-all bg-cover bg-center bg-[#f4f4f4] ${
        active
          ? "border-black shadow-[0_0_0_2px_white,0_0_0_4px_black]"
          : "border-[var(--border)] hover:border-black"
      }`}
      style={
        color.swatch_url ? { backgroundImage: `url(${color.swatch_url})` } : {}
      }
    >
      {badge > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-black text-white font-mono-tech text-[10px] font-bold leading-[18px] text-center">
          {badge}
        </span>
      )}
    </button>
  );
}

// Fallback hex map used when the supplier does not provide a hex code.
// The real render path uses per-color swatch image URLs; this map is
// unknown color renders as a neutral grey swatch.
const COLOR_HEX = {
  Black: "#111111",
  White: "#FFFFFF",
  Navy: "#0B1F44",
  Charcoal: "#36454F",
  Red: "#C8102E",
  "Royal Blue": "#1E4CA1",
  Royal: "#1E4CA1",
  "Sport Grey": "#9BA1A6",
  Forest: "#0B4A2F",
  "Athletic Heather": "#B4B7BA",
  "Dark Heather": "#3F4550",
  "Steel Grey": "#4E5A66",
  "Grey Steel": "#4E5A66",
  Maroon: "#5C0A24",
  Purple: "#4B2E83",
  Ash: "#D8D9D6",
  "Deep Red": "#7B1E28",
  "Safety Yellow": "#EDE939",
  "Safety Orange": "#F26722",
  "Safety Green": "#3EBD75",
  Natural: "#EDE3D0",
  Khaki: "#B8A16A",
  "Postman Blue": "#2E4B6E",
  Kelly: "#0E9F4B",
  Kelly_Green: "#0E9F4B",
  Yellow: "#FFCC00",
  Orange: "#F26722",
  Pink: "#F5A5C0",
  "Light Pink": "#F7C6D3",
  "Hot Pink": "#E9407A",
  Brown: "#5A3B29",
  Cardinal: "#8C1D18",
  "Cardinal Red": "#8C1D18",
  Cyan: "#00A9E0",
  Turquoise: "#22C6C9",
  Teal: "#008E97",
};

function swatchStyle(name, hex) {
  if (hex) return { background: hex };
  if (!name || name === "—") return { background: "#DDDDDD" };
  if (name.includes("/")) {
    const [a, b] = name.split("/").map((s) => s.trim());
    const ca = COLOR_HEX[a] || "#888";
    const cb = COLOR_HEX[b] || "#EEE";
    return {
      background: `linear-gradient(135deg, ${ca} 0 50%, ${cb} 50% 100%)`,
    };
  }
  return { background: COLOR_HEX[name] || "#CCCCCC" };
}

// Preferred size ordering for apparel — everything else lands at the end
// in alphabetical order (bags, caps, OSFA, NoSize, custom sizes, etc.).
const SIZE_ORDER = [
  "XS", "S", "M", "L", "XL", "2XL", "3XL", "4XL", "5XL", "6XL",
  "OSFA", "One Size", "NoSize",
];

function sortSizes(sizes) {
  const idx = (s) => {
    const i = SIZE_ORDER.indexOf(s);
    return i === -1 ? 999 : i;
  };
  return [...sizes].sort((a, b) => {
    const ia = idx(a);
    const ib = idx(b);
    if (ia !== ib) return ia - ib;
    return a.localeCompare(b);
  });
}

// Iter39 · Fase A — volume-price tier structure (no admin UI yet).
// Order matches the user's spec: 1-11 / 12-23 / 24-47 / 48-71 / 72+.
const PRICE_TIERS = [
  { key: "1-11",  label: "1–11",  min: 1,  max: 11  },
  { key: "12-23", label: "12–23", min: 12, max: 23  },
  { key: "24-47", label: "24–47", min: 24, max: 47  },
  { key: "48-71", label: "48–71", min: 48, max: 71  },
  { key: "72+",   label: "72+",   min: 72, max: Infinity },
];

function pickTierPrice(tiers, qty) {
  if (!tiers) return null;
  const t = PRICE_TIERS.find((tr) => qty >= tr.min && qty <= tr.max);
  if (!t) return null;
  const v = parseFloat(tiers[t.key]);
  return isFinite(v) && v > 0 ? v : null;
}

// Iter42 · Fase B — Costo Base auto-tier by qty.
//   qty 1-11   → piece_price
//   qty 12-71  → dozen_price
//   qty 72+    → case_price
// Falls back to the next-tighter tier when a value is missing so a valid
// default is always available.
function pickBaseCost({ piece_price, dozen_price, case_price }, qty) {
  const p = parseFloat(piece_price) || 0;
  const d = parseFloat(dozen_price) || 0;
  const c = parseFloat(case_price) || 0;
  if (qty >= 72 && c > 0) return c;
  if (qty >= 12 && d > 0) return d;
  if (p > 0) return p;
  return d || c || 0;
}

// Iter44 · Fase B — Método × Posición → Costo (per_unit). Fully vendor-facing:
// the seller only picks Method + Position and GP injects the cost silently.
// Vendors NEVER see the amounts (all $$ UI is gated by costs.view). Positions
// are keyed by ORDER (Array to preserve dropdown order).
// Bloque 1 Partner Points — la tabla método×posición→costo ya NO vive en el
// bundle: se carga en runtime desde GET /api/internal-costs (los partners
// reciben solo los NOMBRES de posición, sin montos; el costo se inyecta
// server-side al guardar el Estimate).

// Maps whatever decoration method the admin created in the DB to one of our
// three cost-table keys via case-insensitive substring match. Any DB method
// that does not contain "dtf", "embroider" or "patch" simply returns null →
// no auto-cost applies (retro-compat with Vinyl, Screen Print, UV, etc.).
function resolveDecorationCostKey(dbMethodName) {
  const n = (dbMethodName || "").toLowerCase();
  if (!n) return null;
  if (n.includes("dtf")) return "DTF";
  if (n.includes("embroider")) return "Embroidery";
  if (n.includes("patch")) return "Leather Patch";
  return null;
}

export default function CatalogProductDetail({ summary, onClose, onAdd, readOnly = false }) {
  const { t } = useI18n();
  const { can, isPartner } = useAuth();
  // Gate all financial UI (base cost, extras editor, cost summary) behind
  // costs.view. Regular employees never see these fields, and the backend
  // strips them from any response anyway.
  const canViewCosts = can("costs", "view");
  const [full, setFull] = useState(null);
  const [loading, setLoading] = useState(true);

  // Config state — kept per-color for per-color size steppers.
  const [activeColor, setActiveColor] = useState("");
  const [qtyByColor, setQtyByColor] = useState({}); // { color_name: { size: n } }
  const [activeImage, setActiveImage] = useState(summary?.default_image_url || "");
  const [notes, setNotes] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  // Iter46 · Fase C — Motor Comercial. `unitPriceManual` flips to true when
  // admin overrides the auto price by editing the input. Vendor never sees
  // this input (read-only chip). `commercialQuote` is the last engine result.
  const [unitPriceManual, setUnitPriceManual] = useState(false);
  const [unitCost, setUnitCost] = useState("");
  // Track if the operator manually edited the base cost. Auto-fill from the
  // catalog tier only fires while this stays false.
  const [unitCostTouched, setUnitCostTouched] = useState(false);
  // Iter42 · Fase B — extra_costs editor (only visible to costs.view users).
  // Each entry: { key, concept, amount, type: 'per_unit'|'flat' }.
  const [extraCosts, setExtraCosts] = useState([]);
  const [decorationMethodId, setDecorationMethodId] = useState("");
  const [decorationMethods, setDecorationMethods] = useState([]);
  // Position (logo location) — dropdown from admin catalog + "Otro" free-text
  const [logoLocationId, setLogoLocationId] = useState("");
  const [logoLocationOther, setLogoLocationOther] = useState(false);
  const [logoLocationCustom, setLogoLocationCustom] = useState("");
  const [logoLocations, setLogoLocations] = useState([]);
  // Iter44 · Fase B — when the selected decoration method matches one of the
  // 3 cost-enabled methods (DTF / Embroidery / Leather Patch), the position
  // dropdown is replaced by hardcoded positions from DECORATION_COST_TABLE
  // and the extra_cost is derived automatically. This name is stored so the
  // order carries the position label as `logo_location_custom` (since the
  // hardcoded positions are not DB rows).
  const [autoPositionName, setAutoPositionName] = useState("");
  // Volume-based price tiers — structure only, no admin UI yet. Empty by
  // default; user can fill any of the 5 brackets and the correct one will
  // auto-drive `unit_price` when they set a qty.
  const [priceTiers, setPriceTiers] = useState({});
  const [showTiers, setShowTiers] = useState(false);
  // Bloque 1 — tabla de decoración cargada en runtime (sin costos en bundle).
  const [decorationCostTable, setDecorationCostTable] = useState({});
  // Bloque 1 Partner Points — cotización calculada por el backend.
  const [serverQuote, setServerQuote] = useState(null);

  // Carga runtime de la tabla método×posición (internos: con costos;
  // partner: solo nombres de posición).
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/internal-costs`, { withCredentials: true })
      .then(({ data }) => {
        if (!cancelled) setDecorationCostTable(data?.decoration || {});
      })
      .catch(() => {
        if (!cancelled) setDecorationCostTable({});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch full product on mount from the local mirror + live pricing.
  useEffect(() => {
    if (!summary?.style) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(
        `${API}/catalog/browse/product/${encodeURIComponent(summary.style)}`,
        { withCredentials: true }
      )
      .then(({ data }) => {
        if (cancelled) return;
        setFull(data);
        const firstColor = (data?.colors || [])[0]?.color_name || "";
        setActiveColor(firstColor);
        setActiveImage(
          (data?.colors || [])[0]?.product_image ||
            data?.default_image ||
            summary.default_image_url ||
            ""
        );
      })
      .catch(() => {
        if (!cancelled) {
          toast.error(t("catalog_load_error"));
          setFull(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [summary?.style, summary?.default_image_url, t]);

  // Load decoration methods (internal endpoint — unchanged).
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/decoration-methods?include_inactive=false`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) setDecorationMethods(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setDecorationMethods([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Load logo locations (positions) — same simple-catalog endpoint pattern.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/logo-locations?include_inactive=false`, {
        withCredentials: true,
      })
      .then(({ data }) => {
        if (!cancelled) setLogoLocations(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setLogoLocations([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Derive colors + per-color sizes from the mirror's shape.
  // `full.colors[]` is already deduped and image-enriched by the sync.
  const { colors, colorMeta, sizesByColor, variantMap } = useMemo(() => {
    const cs = full?.colors || [];
    const colorSet = [];
    const meta = {};
    const sizesMap = {};
    const varMap = {};
    for (const c of cs) {
      const cname = c.color_name || c.color_code || "—";
      if (!(cname in meta)) {
        colorSet.push(cname);
        meta[cname] = {
          image: c.product_image || c.swatch_url || null,
          swatch_url: c.swatch_url || null,
          code: c.color_code || cname,
        };
      }
      const sizes = c.sizes && c.sizes.length ? c.sizes : full?.sizes || [];
      sizesMap[cname] = sortSizes(sizes);
      for (const s of sizesMap[cname]) {
        varMap[`${cname}::${s}`] = {
          sku: `${full?.style || ""}-${c.color_code || cname}-${s}`,
          color_name: cname,
          color_code: c.color_code || cname,
          size_name: s,
          image_url: c.product_image || c.swatch_url || null,
        };
      }
    }
    return {
      colors: colorSet,
      colorMeta: meta,
      sizesByColor: sizesMap,
      variantMap: varMap,
    };
  }, [full]);

  // When active color changes, swap the primary image to its swatch photo.
  useEffect(() => {
    if (!activeColor) return;
    const img = colorMeta[activeColor]?.image;
    if (img) setActiveImage(img);
  }, [activeColor, colorMeta]);

  const setCell = (color, size, val) => {
    const n = Math.max(0, parseInt(val || "0", 10) || 0);
    setQtyByColor((prev) => {
      const next = { ...prev, [color]: { ...(prev[color] || {}) } };
      if (n <= 0) {
        delete next[color][size];
      } else {
        next[color][size] = n;
      }
      return next;
    });
  };

  const totalQty = useMemo(() => {
    let total = 0;
    for (const c of Object.keys(qtyByColor)) {
      for (const s of Object.keys(qtyByColor[c] || {})) {
        total += qtyByColor[c][s] || 0;
      }
    }
    return total;
  }, [qtyByColor]);

  const linesCount = useMemo(
    () =>
      Object.values(qtyByColor).reduce(
        (acc, sizeMap) =>
          acc + Object.values(sizeMap).filter((n) => (n || 0) > 0).length,
        0
      ),
    [qtyByColor]
  );

  const totalActiveColor = useMemo(() => {
    const c = qtyByColor[activeColor] || {};
    return Object.values(c).reduce((a, b) => a + (b || 0), 0);
  }, [qtyByColor, activeColor]);

  // Iter42 · Fase B — auto-fill Costo Base from catalog prices tiered by qty.
  //   1-11   → piece_price
  //   12-71  → dozen_price
  //   72+    → case_price
  // Stops as soon as the operator manually edits the field.
  // Fix Jun 2026 — the auto cost MUST load for EVERY role so the pricing
  // engine computes the same selling price (APPAREL = cost×2+6, Beanies =
  // max(floor, cost×mult)). Cost VISIBILITY stays gated by canViewCosts.
  useEffect(() => {
    if (unitCostTouched) return;    // respect manual edits
    if (!full) return;
    const auto = pickBaseCost(full, totalQty || 1);
    if (auto > 0) setUnitCost(String(auto));
  }, [full, totalQty, unitCostTouched]);

  // Aggregate calculations for the cost summary panel (admin/costs-view only).
  const extraCostsTotal = useMemo(() => {
    const q = totalQty || 0;
    return extraCosts.reduce((sum, ec) => {
      const amt = parseFloat(ec.amount) || 0;
      return sum + (ec.type === "flat" ? amt : amt * q);
    }, 0);
  }, [extraCosts, totalQty]);

  // Iter44 · Fase B — derive the current method's cost key (DTF / Embroidery
  // / Leather Patch / null). Used to switch the position dropdown between
  // hardcoded cost positions and the legacy DB-driven logo_locations list.
  const activeMethodCostKey = useMemo(() => {
    const m = decorationMethods.find((x) => x.id === decorationMethodId);
    return resolveDecorationCostKey(m?.name);
  }, [decorationMethodId, decorationMethods]);

  // Iter46 · Fase C — commercial group resolved from SanMar category.
  // Silent for the vendor (never displayed); admins see it in the badge.
  const commercialGroup = useMemo(
    () =>
      resolveCommercialGroup(
        full?.category,
        full?.categories_all || full?.categoriesAll || []
      ),
    [full]
  );

  // Detect Long Sleeve from product name / sub-category (T_SHIRTS only).
  const longSleeveFlag = useMemo(
    () => isLongSleeve(full?.title || full?.product_name, full?.sub_category),
    [full]
  );

  // Iter46 · Fase C — compute the *dominant* size across the qtyByColor grid
  // to feed the size-uplift rule. When quantities span multiple sizes the
  // most-used size wins (deterministic, mirrors what a human quote would
  // do). Returns "" if no size is picked yet.
  const dominantSize = useMemo(() => {
    const totals = {};
    for (const rows of Object.values(qtyByColor || {})) {
      for (const [sz, q] of Object.entries(rows || {})) {
        const n = parseInt(q, 10) || 0;
        if (!n) continue;
        totals[sz] = (totals[sz] || 0) + n;
      }
    }
    let best = "";
    let bestN = -1;
    for (const [sz, n] of Object.entries(totals)) {
      if (n > bestN) {
        best = sz;
        bestN = n;
      }
    }
    return best;
  }, [qtyByColor]);

  // Motor Comercial · quote for the current line item.
  const beanieProduct = isBeanie(full?.title || summary?.name || "");
  // MIX & MATCH (Jun 2026) — pool del producto (TSHIRTS/POLOS/HOODIES/JACKETS/
  // CAPS_*/BEANIES o null). La cantidad combinada del pool valida mínimo y tier.
  const mixPoolMethodName =
    decorationMethods.find((x) => x.id === decorationMethodId)?.name || "";
  const mixPool = resolveMixPool(
    full?.category || summary?.category || "",
    full?.categories_all || full?.categoriesAll || [],
    full?.title || summary?.name || "",
    mixPoolMethodName
  );
  const mixPoolMin = mixPool ? MIX_POOL_MIN[mixPool] : 0;
  const commercialQuote = useMemo(() => {
    if (!commercialGroup) return null;
    const method = decorationMethods.find((x) => x.id === decorationMethodId);
    return calculateSellingPrice({
      group: commercialGroup,
      qty: totalQty || 0,
      baseCost: parseFloat(unitCost) || 0,
      longSleeve: longSleeveFlag,
      size: dominantSize,
      decorationMethod: method?.name || "",
      // MIX & MATCH: permite agregar < mínimo (precio provisional del tier más
      // bajo); el mínimo combinado se valida al guardar el Estimate.
      beanie: beanieProduct,
      combinedQty: mixPool ? Math.max(totalQty || 0, mixPoolMin) : null,
      polo: mixPool === "POLOS",
    });
  }, [
    commercialGroup,
    beanieProduct,
    mixPool,
    mixPoolMin,
    totalQty,
    unitCost,
    longSleeveFlag,
    dominantSize,
    decorationMethodId,
    decorationMethods,
  ]);

  // Bloque 1 Partner Points — el partner NO tiene costos en el navegador:
  // el precio se pide al backend (quote-item con style; costo resuelto en BD).
  const productStyleForQuote = full?.style || summary?.style || "";
  useEffect(() => {
    if (!isPartner || !commercialGroup || !productStyleForQuote) {
      setServerQuote(null);
      return;
    }
    let cancelled = false;
    const tmr = setTimeout(() => {
      axios
        .post(
          `${API}/commercial/quote-item`,
          {
            group: commercialGroup,
            qty: totalQty || 0,
            style: productStyleForQuote,
            size: dominantSize || null,
            long_sleeve: longSleeveFlag,
            decoration_method: mixPoolMethodName || null,
            beanie: beanieProduct,
            combined_qty: mixPool ? Math.max(totalQty || 0, mixPoolMin) : null,
            polo: mixPool === "POLOS",
          },
          { withCredentials: true }
        )
        .then(({ data }) => {
          if (!cancelled) setServerQuote(data);
        })
        .catch(() => {
          if (!cancelled) setServerQuote(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(tmr);
    };
  }, [
    isPartner,
    commercialGroup,
    productStyleForQuote,
    totalQty,
    dominantSize,
    longSleeveFlag,
    mixPoolMethodName,
    beanieProduct,
    mixPool,
    mixPoolMin,
  ]);

  // Auto-drive unitPrice from the engine unless the admin manually overrode.
  // Partner: usa la cotización del backend; internos: motor local (sin cambios).
  const effectiveQuote = isPartner ? serverQuote : commercialQuote;
  useEffect(() => {
    if (unitPriceManual) return;
    if (effectiveQuote && effectiveQuote.ok) {
      setUnitPrice(String(effectiveQuote.unit_price.toFixed(2)));
    }
  }, [effectiveQuote, unitPriceManual]);

  // Iter42/46 · financeSummary — MUST be defined AFTER commercialGroup,
  // longSleeveFlag, and dominantSize to avoid TDZ ReferenceError.
  const financeSummary = useMemo(() => {
    const cost = parseFloat(unitCost) || 0;
    const qty = totalQty || 0;
    const manualPrice = parseFloat(unitPrice) || 0;
    let revenue = 0;
    if (!unitPriceManual && commercialGroup && qty > 0) {
      const method = decorationMethods.find((x) => x.id === decorationMethodId);
      for (const color of Object.keys(qtyByColor || {})) {
        const perSize = qtyByColor[color] || {};
        for (const [sz, q] of Object.entries(perSize)) {
          const n = parseInt(q, 10) || 0;
          if (!n) continue;
          const vq = calculateSellingPrice({
            group: commercialGroup,
            qty,
            baseCost: cost,
            longSleeve: longSleeveFlag,
            size: sz !== "—" ? sz : "",
            decorationMethod: method?.name || "",
            beanie: beanieProduct,
            combinedQty: mixPool ? Math.max(qty || 0, mixPoolMin) : null,
            polo: mixPool === "POLOS",
          });
          revenue += (vq.ok ? vq.unit_price : manualPrice) * n;
        }
      }
    } else {
      revenue = manualPrice * qty;
    }
    const baseCostTotal = cost * qty;
    const costTotal = baseCostTotal + extraCostsTotal;
    const profit = revenue - costTotal;
    const margin = revenue > 0 ? (profit / revenue) * 100 : 0;
    return { revenue, baseCostTotal, extraCostsTotal, costTotal, profit, margin };
  }, [
    unitPrice,
    unitPriceManual,
    unitCost,
    totalQty,
    extraCostsTotal,
    commercialGroup,
    beanieProduct,
    mixPool,
    mixPoolMin,
    qtyByColor,
    longSleeveFlag,
    decorationMethodId,
    decorationMethods,
  ]);

  // Iter44 · Fase B — auto-inject a single extra_cost derived from
  // (method + position). Runs whenever the method or position changes.
  // Vendors never see the amount (all $$ UI is gated by costs.view) but
  // the cost is applied silently and flows through to backend on Add.
  // If the method is NOT one of the 3 cost-enabled methods → extras stay [].
  useEffect(() => {
    if (!activeMethodCostKey || !autoPositionName) {
      // Clear only the auto-injected entry; keep the rest empty (legacy
      // manual extras are no longer editable so extras is always [] or
      // a single auto row).
      setExtraCosts((prev) => (prev.length === 0 ? prev : []));
      return;
    }
    const positions = decorationCostTable[activeMethodCostKey] || [];
    const hit = positions.find((p) => p.name === autoPositionName);
    if (!hit || typeof hit.cost !== "number") {
      // Partner (sin montos) o posición desconocida: el costo lo inyecta el
      // backend al guardar; no se arrastra ningún extra desde el navegador.
      setExtraCosts([]);
      return;
    }
    setExtraCosts([
      {
        key: `auto-${activeMethodCostKey}-${hit.name}`,
        concept: `${activeMethodCostKey} · ${hit.name}`,
        amount: hit.cost,
        type: "per_unit",
        auto: true,
      },
    ]);
  }, [activeMethodCostKey, autoPositionName, decorationCostTable]);

  // Reset the auto position whenever the method changes (or clears) so a
  // stale position from a different method never applies its cost.
  useEffect(() => {
    setAutoPositionName("");
  }, [decorationMethodId]);

  const handleAdd = async () => {
    if (totalQty <= 0) {
      toast.error(t("catalog_detail_qty_required"));
      return;
    }
    const manualPrice = parseFloat(unitPrice) || 0;
    const cost = parseFloat(unitCost) || 0;
    const brand = full?.brand || summary?.brand || "";
    // Normalize extra_costs list before sending to backend — the backend
    // will re-normalize but this keeps payloads slim and predictable.
    const extras = extraCosts
      .filter((ec) => (ec.concept || "").trim() || parseFloat(ec.amount) > 0)
      .map((ec) => ({
        concept: (ec.concept || "").trim(),
        amount: parseFloat(ec.amount) || 0,
        type: ec.type === "flat" ? "flat" : "per_unit",
      }));
    // "Otro" convention: when the user typed a custom location but did not
    // pick from the dropdown, we still keep logo_location_id=null and pass
    // the free-text via logo_location_custom.
    // Iter46 · Fase C — resolve the current decoration method name once so
    // per-variant engine calls stay cheap and consistent with the header quote.
    const methodForEngine = decorationMethods.find(
      (x) => x.id === decorationMethodId
    );
    const methodNameForEngine = methodForEngine?.name || "";

    // Bloque 1 Partner Points — precios por talla calculados por el backend
    // (una llamada por talla distinta con cantidad > 0).
    let serverPriceBySize = null;
    if (isPartner && commercialGroup) {
      const distinctSizes = new Set();
      for (const color of colors) {
        const perSize = qtyByColor[color] || {};
        for (const size of sizesByColor[color] || []) {
          if ((perSize[size] || 0) > 0) distinctSizes.add(size);
        }
      }
      const resps = await Promise.all(
        [...distinctSizes].map((sz) =>
          axios
            .post(
              `${API}/commercial/quote-item`,
              {
                group: commercialGroup,
                qty: totalQty || 0,
                style: full?.style || summary?.style || "",
                size: sz !== "—" ? sz : "",
                long_sleeve: longSleeveFlag,
                decoration_method: methodNameForEngine || null,
                beanie: beanieProduct,
                combined_qty: mixPool ? Math.max(totalQty || 0, mixPoolMin) : null,
                polo: mixPool === "POLOS",
              },
              { withCredentials: true }
            )
            .then((r) => [sz, r.data])
            .catch(() => [sz, null])
        )
      );
      serverPriceBySize = Object.fromEntries(resps);
    }

    const items = [];
    for (const color of colors) {
      const perSize = qtyByColor[color] || {};
      for (const size of sizesByColor[color] || []) {
        const n = perSize[size] || 0;
        if (n <= 0) continue;
        const variant = variantMap[`${color}::${size}`] || {};

        // Iter46 · Fase C fix — per-variant price MUST include size uplifts
        // (2XL/3XL/4XL). The tier is still driven by the ORDER-level total
        // quantity (totalQty) so all variants share the same bracket, but the
        // uplift is size-specific, so 2XL cells price at tier + $3 while M
        // cells stay at tier base.
        //
        // Precedence (highest → lowest):
        //   1. Admin manual override (unitPriceManual) → use manualPrice
        //      verbatim for every variant. Preserves the "MANUAL OVERRIDE"
        //      contract: admin has the last word.
        //   2. Motor Comercial (Fase C) → recompute per-variant using the
        //      cell's specific size for the uplift.
        //   3. Legacy manual tier table (priceTiers) → back-compat for old
        //      catalog flows where the operator typed the tier grid by hand.
        //   4. Fallback → manualPrice (whatever's currently in the input).
        let finalPrice = manualPrice;
        let variantUplifts =
          (effectiveQuote && effectiveQuote.applied_uplifts) || [];
        let variantTierLabel =
          (effectiveQuote && effectiveQuote.tier_label) || null;
        if (unitPriceManual) {
          finalPrice = manualPrice;
        } else if (isPartner && serverPriceBySize) {
          const sq = serverPriceBySize[size];
          if (sq?.ok) {
            finalPrice = sq.unit_price;
            variantUplifts = sq.applied_uplifts || [];
            variantTierLabel = sq.tier_label || null;
          }
        } else if (commercialGroup) {
          const variantQuote = calculateSellingPrice({
            group: commercialGroup,
            qty: totalQty || 0,
            baseCost: cost,
            longSleeve: longSleeveFlag,
            size: size !== "—" ? size : "",
            decorationMethod: methodNameForEngine,
            beanie: beanieProduct,
            combinedQty: mixPool ? Math.max(totalQty || 0, mixPoolMin) : null,
            polo: mixPool === "POLOS",
          });
          if (variantQuote.ok) {
            finalPrice = variantQuote.unit_price;
            variantUplifts = variantQuote.applied_uplifts;
            variantTierLabel = variantQuote.tier_label;
          } else {
            const tierPrice = pickTierPrice(priceTiers, n);
            finalPrice = tierPrice != null ? tierPrice : manualPrice;
          }
        } else {
          const tierPrice = pickTierPrice(priceTiers, n);
          finalPrice = tierPrice != null ? tierPrice : manualPrice;
        }
        items.push({
          product_code: variant.sku || `${full?.style || summary.style}-${color}-${size}`,
          description: full?.title || summary.name || "",
          quantity: n,
          // GP-002 fix: mirror the operator-typed notes into BOTH fields.
          //   • `notes`          → the visible "Notas" input on the order row
          //   • `customer_notes` → the field the PDF renders as "↳ note…"
          // Writing both preserves every piece of info the user configured in
          // the catalog step and keeps PDF rendering intact.
          notes: notes || "",
          customer_notes: notes || "",
          unit_price: finalPrice,
          unit_cost: cost,
          price_manual: unitPriceManual || false,
          source_type: "catalog",
          catalog_product_id: null,
          catalog_sku: variant.sku || "",
          catalog_color: color !== "—" ? color : "",
          catalog_size: size !== "—" ? size : "",
          catalog_brand: brand,
          catalog_image_url:
            variant.image_url ||
            colorMeta[color]?.image ||
            full?.default_image ||
            summary.default_image_url ||
            "",
          decoration_method_id: decorationMethodId || null,
          logo_location_id: activeMethodCostKey ? null : (logoLocationId || null),
          // Iter44 · Fase B — when method is cost-enabled we ship the auto
          // position NAME through logo_location_custom (hardcoded positions
          // are not DB rows). Retro-compat: legacy methods keep using the
          // manual "Otro…" free-text via the same field.
          logo_location_custom: activeMethodCostKey
            ? (autoPositionName || "").trim()
            : (logoLocationCustom || "").trim(),
          // Iter42 · Fase B — carry per-product extra costs (empty list is
          // fine; backend will keep the field as []).
          extra_costs: extras,
          // Snapshot the tier table only when the user actually filled any
          // bracket. Keeps legacy items unchanged and payloads slim.
          price_tiers:
            Object.values(priceTiers).some((v) => parseFloat(v) > 0)
              ? { ...priceTiers }
              : null,
          // Iter46 · Fase C — Motor Comercial snapshot per item for audit.
          //   commercial_group / tier / uplifts describe how unit_price was
          //   priced by the engine. manual_price_override records if admin
          //   edited unit_price by hand (badge shows in the UI).
          commercial_group: commercialGroup || null,
          commercial_tier: variantTierLabel,
          commercial_uplifts: variantUplifts,
          manual_price_override: unitPriceManual,
          // MIX & MATCH (Jun 2026) — pool para agregación en EstimateForm.
          mix_pool: mixPool || null,
          // Bloque 1 Partner Points — estilo SanMar para re-validación
          // server-side del precio/costo (fuente confiable: BD).
          catalog_style: full?.style || summary?.style || "",
        });
      }
    }
    onAdd(items);
    toast.success(
      t("catalog_detail_added_toast").replace("{n}", items.length)
    );
  };

  const activeQtyMap = qtyByColor[activeColor] || {};
  const activeSizes = sizesByColor[activeColor] || [];
  const productName = full?.title || summary?.name || "";
  const productBrand = full?.brand || summary?.brand || "";
  const productCategory = full?.category || summary?.category || "";
  const productDescription = full?.description || "";
  const productStyle = full?.style || summary?.style || "";

  return (
    <div
      data-testid="catalog-detail-overlay"
      className="fixed inset-0 z-[60] bg-white flex flex-col"
    >
      <div
        data-testid="catalog-detail-modal"
        className="bg-white w-full h-full flex flex-col overflow-hidden"
      >
        {/* HEADER */}
        <header className="px-4 sm:px-6 py-3 border-b border-black flex items-center justify-between bg-white flex-shrink-0">
          <button
            type="button"
            data-testid="catalog-detail-back"
            onClick={onClose}
            className="font-display text-xs font-bold border border-black px-3 py-1.5 hover:bg-black hover:text-white transition-colors"
          >
            ← {t("catalog_back") || "Volver"}
          </button>
          <div className="font-mono-tech text-[0.65rem] font-bold tracking-[0.2em] text-[var(--text-secondary)] hidden sm:block">
            GP / CATÁLOGO / {productStyle}
          </div>
          <button
            type="button"
            data-testid="catalog-detail-close"
            onClick={onClose}
            className="font-mono-tech text-xs font-bold px-3 py-2 border border-black hover:bg-black hover:text-white transition-colors"
          >
            ✕
          </button>
        </header>

        {/* BODY */}
        <div className="flex-1 min-h-0 overflow-auto">
          {loading && (
            <div
              data-testid="catalog-detail-loading"
              className="p-8 text-center"
            >
              <div className="text-4xl mb-3 animate-pulse">⏳</div>
              <div className="font-display text-lg font-bold">
                {t("catalog_searching")}
              </div>
              <p className="text-sm text-[var(--text-secondary)] mt-2">
                {t("catalog_detail_live_pricing_hint")}
              </p>
            </div>
          )}

          {!loading && !full && (
            <div className="p-8 text-center text-[var(--text-secondary)]">
              {t("catalog_load_error")} {productStyle}.
            </div>
          )}

          {!loading && full && (
            <div className="max-w-7xl mx-auto p-4 sm:p-5">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5">
                {/* LEFT · image + gallery */}
                <div className="lg:col-span-4">
                  <div
                    className="bg-[var(--surface-alt)] border border-[var(--border)] overflow-hidden mx-auto"
                    style={{ maxWidth: 320 }}
                  >
                    <div className="aspect-[4/3]">
                      {activeImage ? (
                        <img
                          src={activeImage}
                          alt={productName}
                          onError={(e) => {
                            e.currentTarget.style.display = "none";
                          }}
                          className="w-full h-full object-cover"
                          data-testid="catalog-detail-image"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-sm text-[var(--text-secondary)]">
                          No image
                        </div>
                      )}
                    </div>
                  </div>
                  {/* Gallery: aggregate every distinct image URL the mirror
                      exposes for the active color (product, front/back/side
                      model, front/back flat). Purely additive — legacy
                      products without extra angles will just show one. */}
                  {(() => {
                    const active = (full?.colors || []).find(
                      (c) => (c.color_name || c.color_code || "—") === activeColor
                    ) || {};
                    const gallery = [
                      active.product_image,
                      active.front_model_image,
                      active.back_model_image,
                      active.side_model_image,
                      active.front_flat_image,
                      active.back_flat_image,
                      full?.default_image,
                    ].filter((u, i, arr) => u && arr.indexOf(u) === i);
                    if (gallery.length <= 1) return null;
                    return (
                      <div
                        className="mt-2 flex flex-wrap gap-1.5 justify-center"
                        data-testid="catalog-detail-gallery"
                      >
                        {gallery.map((u, i) => (
                          <button
                            key={u}
                            type="button"
                            data-testid={`catalog-detail-gallery-thumb-${i}`}
                            onClick={() => setActiveImage(u)}
                            className={`w-14 h-14 border-2 overflow-hidden ${
                              activeImage === u
                                ? "border-black"
                                : "border-[var(--border)] hover:border-black"
                            }`}
                          >
                            <img
                              src={u}
                              alt=""
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                {/* RIGHT · info + controls */}
                <div className="lg:col-span-8 space-y-3.5">
                  {/* Brand + Name + Price */}
                  <div>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        {productBrand && (
                          <span
                            data-testid="catalog-detail-brand"
                            className="inline-block font-mono-tech text-[0.65rem] font-bold tracking-[0.22em] px-2 py-0.5 bg-black text-white"
                          >
                            {productBrand}
                          </span>
                        )}
                        <h1
                          data-testid="catalog-detail-name"
                          className="font-display text-2xl sm:text-3xl font-black tracking-tight leading-[1.05] mt-1.5"
                        >
                          {productName}
                        </h1>
                        <div className="mt-1.5 flex items-center gap-2 flex-wrap font-mono-tech text-[0.7rem] font-bold tracking-[0.15em] text-[var(--text-secondary)]">
                          <span data-testid="catalog-detail-sku">
                            STYLE · {productStyle}
                          </span>
                          {productCategory && (
                            <>
                              <span>·</span>
                              <span>{productCategory}</span>
                            </>
                          )}
                          <span>·</span>
                          <span className="text-[var(--text-secondary)]">
                            {colors.length} colores · {Object.values(sizesByColor).reduce((a, arr) => a + arr.length, 0)} variantes
                          </span>
                        </div>
                      </div>

                      {/* Selling price + Cost */}
                      <div className="flex-shrink-0 flex gap-2">
                        <div className="w-40 border border-black bg-white p-2">
                          <div className="label-overline mb-0.5 flex items-center justify-between">
                            <span>{t("catalog_detail_selling_price")}</span>
                            {/* Iter46 · Fase C — Motor Comercial badge */}
                            {effectiveQuote && effectiveQuote.ok && !unitPriceManual && (
                              <span
                                data-testid="catalog-detail-price-auto-badge"
                                className="ml-1 font-mono-tech text-[8px] font-bold border border-black px-1 leading-none py-0.5"
                                title={`Motor: ${effectiveQuote.group} · ${effectiveQuote.tier_label}`}
                              >
                                AUTO
                              </span>
                            )}
                            {unitPriceManual && canViewCosts && (
                              <span
                                data-testid="catalog-detail-price-override-badge"
                                className="ml-1 font-mono-tech text-[8px] font-bold px-1 leading-none py-0.5"
                                style={{
                                  background: "var(--alert-overdue)",
                                  color: "white",
                                }}
                                title="Precio editado manualmente por admin"
                              >
                                MANUAL OVERRIDE
                              </span>
                            )}
                          </div>
                          {canViewCosts ? (
                            <div className="flex items-center">
                              <span className="font-display text-2xl font-black leading-none">
                                $
                              </span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={unitPrice}
                                onChange={(e) => {
                                  setUnitPrice(e.target.value);
                                  setUnitPriceManual(true);
                                }}
                                placeholder="0.00"
                                data-testid="catalog-detail-price"
                                className="w-full pl-1 font-display text-2xl font-black leading-none focus:outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                              {unitPriceManual && (
                                <button
                                  type="button"
                                  data-testid="catalog-detail-price-reset-auto"
                                  onClick={() => setUnitPriceManual(false)}
                                  className="ml-1 text-[10px] font-mono-tech font-bold border border-black px-1 hover:bg-black hover:text-white transition-colors"
                                  title="Reset to auto price"
                                >
                                  ↺
                                </button>
                              )}
                            </div>
                          ) : (
                            <div
                              data-testid="catalog-detail-price"
                              className="flex items-center font-display text-2xl font-black leading-none"
                            >
                              <span>$</span>
                              <span className="pl-1">
                                {(parseFloat(unitPrice) || 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {effectiveQuote &&
                            !effectiveQuote.ok &&
                            effectiveQuote.reason?.startsWith("min_qty") && (
                              <div
                                data-testid="catalog-detail-min-qty-warning"
                                className="text-[9px] font-mono-tech font-bold text-[var(--alert-overdue)] mt-0.5 leading-tight"
                              >
                                Mín. {effectiveQuote.min_qty} unidades
                              </div>
                            )}
                          {effectiveQuote &&
                            effectiveQuote.ok &&
                            effectiveQuote.applied_uplifts?.length > 0 &&
                            canViewCosts && (
                              <div
                                data-testid="catalog-detail-price-uplifts"
                                className="text-[9px] font-mono-tech text-[var(--text-secondary)] mt-0.5 leading-tight"
                              >
                                {effectiveQuote.applied_uplifts
                                  .map((u) => `+$${u.amount.toFixed(2)} ${u.name}`)
                                  .join(" · ")}
                              </div>
                            )}
                        </div>
                        {canViewCosts && (
                          <div className="w-28 border border-[var(--border)] bg-[var(--surface-alt)] p-2">
                            <div className="label-overline mb-0.5">
                              {t("catalog_detail_base_cost") || "Costo Base"}
                            </div>
                            <div className="flex items-center">
                              <span className="font-display text-xl font-black leading-none text-[var(--text-secondary)]">
                                $
                              </span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={unitCost}
                                onChange={(e) => {
                                  setUnitCost(e.target.value);
                                  setUnitCostTouched(true);
                                }}
                                placeholder="0.00"
                                data-testid="catalog-detail-cost"
                                className="w-full pl-1 font-display text-xl font-black leading-none focus:outline-none appearance-none bg-transparent [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>
                            <div className="text-[9px] font-mono-tech text-[var(--text-secondary)] mt-0.5 leading-tight">
                              {unitCostTouched ? "Manual" : "Auto (tier x qty)"}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {productDescription && (
                      <p
                        data-testid="catalog-detail-description"
                        className="text-xs sm:text-sm text-[var(--text-secondary)] mt-2.5 leading-snug line-clamp-3"
                      >
                        {productDescription}
                      </p>
                    )}
                  </div>

                  <div className="h-px bg-[var(--border)]" />

                  {/* Colors */}
                  <div>
                    <div className="label-overline mb-1.5 flex items-center justify-between">
                      <span>
                        {t("catalog_detail_color")} ·{" "}
                        <span
                          className="text-black"
                          data-testid="catalog-detail-active-color-label"
                        >
                          {activeColor || "—"}
                        </span>
                      </span>
                      <span className="font-mono-tech text-[0.65rem] text-[var(--text-secondary)]">
                        {colors.length} {colors.length === 1 ? "color" : "colores"}
                      </span>
                    </div>
                    <div
                      className="flex flex-wrap gap-2 max-h-40 overflow-auto"
                      data-testid="catalog-detail-colors"
                    >
                      {colors.map((c) => {
                        const isActive = c === activeColor;
                        const colorTotal =
                          Object.values(qtyByColor[c] || {}).reduce(
                            (a, b) => a + (b || 0),
                            0
                          ) || 0;
                        const testKey = c
                          .replace(/\s+/g, "-")
                          .replace(/\//g, "_");
                        return (
                          <button
                            key={c}
                            type="button"
                            data-testid={`catalog-detail-color-${testKey}`}
                            onClick={() => setActiveColor(c)}
                            title={c}
                            aria-label={c}
                            aria-pressed={isActive}
                            className={`relative w-9 h-9 rounded-full border-2 overflow-hidden bg-cover bg-center bg-[#f4f4f4] transition-all ${
                              isActive
                                ? "border-black shadow-[0_0_0_2px_white,0_0_0_4px_black]"
                                : "border-[var(--border)] hover:border-black"
                            }`}
                            style={
                              colorMeta[c]?.swatch_url
                                ? { backgroundImage: `url(${colorMeta[c].swatch_url})` }
                                : swatchStyle(c, null)
                            }
                          >
                            {colorTotal > 0 && (
                              <span
                                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-black text-white font-mono-tech text-[10px] font-bold leading-[18px] text-center"
                                data-testid={`catalog-detail-color-badge-${testKey}`}
                              >
                                {colorTotal}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Sizes for active color */}
                  <div>
                    <div className="label-overline mb-1 flex items-center justify-between">
                      <span>{t("catalog_detail_select_sizes")}</span>
                      <span className="font-mono-tech text-[0.65rem] text-[var(--text-secondary)]">
                        {t("catalog_detail_active_color_total")}:{" "}
                        {totalActiveColor}
                      </span>
                    </div>
                    <div className="text-[0.65rem] text-[var(--text-secondary)] mb-2">
                      {t("catalog_detail_tap_size_hint")}
                    </div>
                    <div
                      className="flex flex-wrap gap-2"
                      data-testid="catalog-detail-sizes"
                    >
                      {activeSizes.map((s) => {
                        const val = activeQtyMap[s] || 0;
                        const selected = val > 0;
                        const testKey = `${activeColor
                          .replace(/\s+/g, "-")
                          .replace(/\//g, "_")}-${s
                          .replace(/\s+/g, "-")
                          .replace(/\//g, "_")}`;
                        return (
                          <div
                            key={s}
                            data-testid={`catalog-detail-size-cell-${testKey}`}
                            className={`inline-flex items-stretch border transition-colors ${
                              selected
                                ? "border-black bg-black text-white"
                                : "border-[var(--border)] bg-white hover:border-black"
                            }`}
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setCell(activeColor, s, selected ? 0 : 1)
                              }
                              data-testid={`catalog-detail-size-${testKey}`}
                              aria-pressed={selected}
                              className="min-w-[48px] px-2.5 py-1.5 font-mono-tech text-xs font-bold tracking-[0.15em] leading-none"
                            >
                              {s}
                            </button>
                            {selected && (
                              <div className="flex items-stretch border-l border-white/30">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCell(activeColor, s, Math.max(0, val - 1))
                                  }
                                  data-testid={`catalog-detail-size-dec-${testKey}`}
                                  aria-label="minus"
                                  className="px-2 hover:bg-white/15 font-bold"
                                >
                                  −
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  value={val}
                                  onChange={(e) =>
                                    setCell(activeColor, s, e.target.value)
                                  }
                                  data-testid={`catalog-qty-${testKey}`}
                                  className="w-10 bg-transparent text-center font-mono-tech text-sm font-bold text-white focus:outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    setCell(activeColor, s, val + 1)
                                  }
                                  data-testid={`catalog-detail-size-inc-${testKey}`}
                                  aria-label="plus"
                                  className="px-2 hover:bg-white/15 font-bold"
                                >
                                  +
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Decoration Method + Position + Notes */}
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                    <div>
                      <div className="label-overline mb-1">
                        {t("catalog_detail_decoration_method") ||
                          "Método de decoración"}
                      </div>
                      <select
                        value={decorationMethodId}
                        onChange={(e) => setDecorationMethodId(e.target.value)}
                        data-testid="catalog-detail-decoration-method"
                        className="w-full px-2.5 py-1.5 border border-[var(--border)] focus:border-black focus:outline-none text-sm bg-white"
                      >
                        <option value="">{`— ${t(
                          "catalog_detail_select_placeholder"
                        )} —`}</option>
                        {decorationMethods.map((m) => (
                          <option key={m.id} value={m.id}>
                            {String(m.name || "")}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="label-overline mb-1">
                        {t("catalog_detail_logo_location") || "Posición"}
                      </div>
                      {/* Iter44 · Fase B — when method is cost-enabled
                          (DTF / Embroidery / Leather Patch), the position
                          dropdown is REPLACED with hardcoded positions from
                          DECORATION_COST_TABLE. Otherwise the legacy DB
                          list is preserved for retro-compat. */}
                      {activeMethodCostKey ? (
                        <select
                          value={autoPositionName}
                          onChange={(e) => setAutoPositionName(e.target.value)}
                          data-testid="catalog-detail-logo-location"
                          className="w-full px-2.5 py-1.5 border border-[var(--border)] focus:border-black focus:outline-none text-sm bg-white"
                        >
                          <option value="">{`— ${t(
                            "catalog_detail_select_placeholder"
                          )} —`}</option>
                          {(decorationCostTable[activeMethodCostKey] || []).map(
                            (p) => (
                              <option key={p.name} value={p.name}>
                                {p.name}
                              </option>
                            )
                          )}
                        </select>
                      ) : (
                        <>
                          <select
                            value={logoLocationOther ? "__other__" : logoLocationId}
                            onChange={(e) => {
                              const v = e.target.value;
                              if (v === "__other__") {
                                setLogoLocationOther(true);
                                setLogoLocationId("");
                              } else {
                                setLogoLocationOther(false);
                                setLogoLocationId(v);
                                setLogoLocationCustom("");
                              }
                            }}
                            data-testid="catalog-detail-logo-location"
                            className="w-full px-2.5 py-1.5 border border-[var(--border)] focus:border-black focus:outline-none text-sm bg-white"
                          >
                            <option value="">{`— ${t(
                              "catalog_detail_select_placeholder"
                            )} —`}</option>
                            {logoLocations.map((l) => (
                              <option key={l.id} value={l.id}>
                                {String(l.name || "")}
                              </option>
                            ))}
                            <option value="__other__">Otro…</option>
                          </select>
                          {logoLocationOther && (
                            <input
                              type="text"
                              value={logoLocationCustom}
                              onChange={(e) => setLogoLocationCustom(e.target.value)}
                              placeholder="Ubicación personalizada"
                              data-testid="catalog-detail-logo-location-custom"
                              className="w-full mt-1.5 px-2.5 py-1.5 border border-[var(--border)] focus:border-black focus:outline-none text-sm"
                            />
                          )}
                        </>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <div className="label-overline mb-1">
                        {t("catalog_detail_notes")}
                      </div>
                      <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        data-testid="catalog-detail-notes"
                        className="w-full px-2.5 py-1.5 border border-[var(--border)] focus:border-black focus:outline-none text-sm resize-none"
                        placeholder="—"
                      />
                    </div>
                  </div>

                  {/* Iter44 · Fase B — Costos Adicionales (read-only, admin/costs.view only).
                      The vendor no longer edits costs manually — GP derives a single
                      per-unit extra from (Method × Position) using DECORATION_COST_TABLE
                      and shows it here purely for admin visibility. */}
                  {canViewCosts && (
                    <div className="border-t border-[var(--border)] pt-3 mt-1" data-testid="catalog-detail-extras-section">
                      <div className="label-overline mb-2">
                        Costos Adicionales (auto)
                      </div>
                      {extraCosts.length === 0 ? (
                        <div className="text-[0.7rem] font-mono-tech text-[var(--text-secondary)] italic" data-testid="catalog-detail-extras-empty">
                          {activeMethodCostKey
                            ? "Selecciona una posición para aplicar el costo automático."
                            : "El costo adicional se aplicará automáticamente al elegir Método (DTF, Embroidery o Leather Patch) + Posición."}
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {extraCosts.map((ec, i) => (
                            <div
                              key={ec.key || i}
                              data-testid={`catalog-detail-extra-row-${i}`}
                              className="flex items-center justify-between border border-[var(--border)] bg-[var(--surface-alt)] px-3 py-2"
                            >
                              <span className="font-mono-tech text-xs">
                                <span className="font-bold">{ec.concept}</span>
                                <span className="ml-2 text-[var(--text-secondary)]">
                                  · Por unidad
                                </span>
                              </span>
                              <span
                                data-testid={`catalog-detail-extra-amount-${i}`}
                                className="font-mono-tech text-sm font-bold tabular-nums"
                              >
                                ${(Number(ec.amount) || 0).toFixed(2)}/u
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Iter42 · Fase B — Resumen Financiero (costs.view only) */}
                  {canViewCosts && (
                    <div
                      className="border-t border-[var(--border)] pt-3 mt-1"
                      data-testid="catalog-detail-finance-summary"
                    >
                      <div className="label-overline mb-2">
                        Resumen Financiero
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-6 gap-2">
                        <div className="border border-[var(--border)] bg-white p-2">
                          <div className="label-overline mb-0.5">Ingreso</div>
                          <div
                            data-testid="catalog-detail-finance-revenue"
                            className="font-mono-tech text-sm font-bold tabular-nums"
                          >
                            ${financeSummary.revenue.toFixed(2)}
                          </div>
                        </div>
                        <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-2">
                          <div className="label-overline mb-0.5">Costo Base</div>
                          <div
                            data-testid="catalog-detail-finance-base-cost"
                            className="font-mono-tech text-sm tabular-nums"
                          >
                            ${financeSummary.baseCostTotal.toFixed(2)}
                          </div>
                        </div>
                        <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-2">
                          <div className="label-overline mb-0.5">Extras</div>
                          <div
                            data-testid="catalog-detail-finance-extras"
                            className="font-mono-tech text-sm tabular-nums"
                          >
                            ${financeSummary.extraCostsTotal.toFixed(2)}
                          </div>
                        </div>
                        <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-2">
                          <div className="label-overline mb-0.5">Costo Total</div>
                          <div
                            data-testid="catalog-detail-finance-cost-total"
                            className="font-mono-tech text-sm font-bold tabular-nums"
                          >
                            ${financeSummary.costTotal.toFixed(2)}
                          </div>
                        </div>
                        <div
                          className="border p-2"
                          style={{
                            borderColor:
                              financeSummary.profit >= 0
                                ? "var(--success)"
                                : "var(--alert-overdue)",
                            background: "white",
                          }}
                        >
                          <div className="label-overline mb-0.5">Ganancia</div>
                          <div
                            data-testid="catalog-detail-finance-profit"
                            className="font-mono-tech text-sm font-bold tabular-nums"
                            style={{
                              color:
                                financeSummary.profit >= 0
                                  ? "var(--success)"
                                  : "var(--alert-overdue)",
                            }}
                          >
                            ${financeSummary.profit.toFixed(2)}
                          </div>
                        </div>
                        <div className="border border-[var(--border)] bg-white p-2">
                          <div className="label-overline mb-0.5">Margen</div>
                          <div
                            data-testid="catalog-detail-finance-margin"
                            className="font-mono-tech text-sm font-bold tabular-nums"
                          >
                            {financeSummary.margin.toFixed(2)}%
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Volume-based price tiers (structure only, no admin) */}
                  <div className="border-t border-[var(--border)] pt-3 mt-1">
                    <button
                      type="button"
                      onClick={() => setShowTiers((s) => !s)}
                      data-testid="catalog-detail-tiers-toggle"
                      className="label-overline hover:text-black text-[var(--text-secondary)] flex items-center gap-1"
                    >
                      {showTiers ? "▾" : "▸"} Precios por cantidad (opcional)
                    </button>
                    {showTiers && (
                      <div className="mt-2 grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="catalog-detail-tiers">
                        {PRICE_TIERS.map((tier) => (
                          <label key={tier.key} className="block">
                            <div className="label-overline mb-0.5">{tier.label}</div>
                            <div className="flex items-center border border-[var(--border)] focus-within:border-black">
                              <span className="pl-1.5 text-sm text-[var(--text-secondary)]">$</span>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={priceTiers[tier.key] || ""}
                                onChange={(e) =>
                                  setPriceTiers((p) => ({
                                    ...p,
                                    [tier.key]: e.target.value,
                                  }))
                                }
                                placeholder="0.00"
                                data-testid={`catalog-detail-tier-${tier.key}`}
                                className="w-full px-1 py-1 font-mono-tech text-sm focus:outline-none appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                              />
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* STICKY FOOTER */}
        <footer className="border-t border-black bg-white px-4 sm:px-6 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 flex-shrink-0">
          <div className="flex items-center gap-4 sm:gap-6">
            <div>
              <div className="label-overline">
                {t("catalog_detail_total_qty")}
              </div>
              <div
                className="font-display text-2xl font-black tracking-tighter leading-none"
                data-testid="catalog-detail-total-qty"
              >
                {totalQty}
              </div>
            </div>
            <div className="h-8 w-px bg-[var(--border)]" />
            <div>
              <div className="label-overline">
                {t("catalog_detail_lines_preview")}
              </div>
              <div
                className="font-display text-2xl font-black tracking-tighter leading-none"
                data-testid="catalog-detail-lines-count"
              >
                {linesCount}
              </div>
            </div>
            <div className="h-8 w-px bg-[var(--border)] hidden sm:block" />
            <div className="hidden sm:block">
              <div className="label-overline">
                {t("catalog_detail_selling_price")}
              </div>
              <div
                className="font-display text-2xl font-black tracking-tighter leading-none"
                data-testid="catalog-detail-footer-price"
              >
                ${(parseFloat(unitPrice) || 0).toFixed(2)}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              data-testid="catalog-detail-cancel"
              onClick={onClose}
              className="font-display text-sm font-bold border border-black px-4 py-2.5 hover:bg-[var(--bg)] transition-colors"
            >
              {t("cancel")}
            </button>
            {!readOnly && (
            <button
              type="button"
              data-testid="catalog-detail-add"
              onClick={handleAdd}
              disabled={totalQty <= 0 || loading}
              className="font-display text-sm font-bold bg-black text-white px-6 sm:px-8 py-2.5 hover:bg-[var(--text-secondary)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {t("catalog_detail_add_to_order")}
              {totalQty > 0 && (
                <span className="ml-2 font-mono-tech opacity-80">
                  · {totalQty}
                </span>
              )}
            </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
